import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge Tailwind classes with conflict resolution.
 * Combines clsx (conditional classes) with tailwind-merge (dedup/override).
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Extract initials from a display name.
 * "Max Mustermann" → "MM", "admin" → "AD", "" → "??"
 */
export function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  const words = trimmed.split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/**
 * Resolve an asset path relative to NEXT_PUBLIC_BASE_PATH.
 * Handles trailing/leading slashes safely.
 */
/**
 * The base path for the current app (from NEXT_PUBLIC_BASE_PATH).
 */
export const basePath: string = (
  typeof process !== "undefined"
    ? process.env?.NEXT_PUBLIC_BASE_PATH
    : undefined
) ?? "";

export function assetPath(path: string): string {
  const base = (
    typeof process !== "undefined"
      ? process.env?.NEXT_PUBLIC_BASE_PATH
      : undefined
  ) ?? "";
  if (!base) return path;
  const cleanBase = base.replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
}

/**
 * Derive a display label from user data.
 * Prefers displayName, falls back to email username, then "Account".
 */
export function getDisplayLabel(displayName?: string | null, email?: string | null): string {
  if (displayName?.trim()) return displayName.trim();
  if (email) return email.split("@")[0];
  return "Account";
}

/**
 * Check if a redirect URL is safe.
 * Accepts:
 * - Relative paths starting with "/" (same-origin)
 * - Absolute https:// URLs on a trusted Janeway domain (cross-subdomain SSO)
 *   Trusted domains: janeway.io (prod/beta), janewaylabs.com (dev)
 */
const TRUSTED_REDIRECT_DOMAINS = ["janeway.io", "janewaylabs.com"];

export function isSafeRedirect(url: string): boolean {
  if (!url || typeof url !== "string") return false;
  // Browsers normalize "\" to "/", so "/\evil.com" resolves to the
  // protocol-relative "//evil.com" → attacker origin. Reject any backslash
  // outright before the relative-path check (no legitimate redirect needs one).
  if (url.includes("\\")) return false;
  // Same class of bug, one layer deeper: the URL parser STRIPS tab, CR and LF
  // before parsing. "/\t/evil.com" passes the startsWith("//") check here and
  // becomes "//evil.com" in the browser — verified: it lands on https://evil.com/.
  // Any control character in a redirect target is an attack, never a legitimate
  // path, so reject the whole class instead of the three known payloads.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(url)) return false;
  // Relative path — safe as long as it doesn't start with "//"
  if (url.startsWith("/") && !url.startsWith("//")) return true;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return false;
    const host = parsed.hostname;
    return TRUSTED_REDIRECT_DOMAINS.some(
      (d) => host === d || host.endsWith(`.${d}`)
    );
  } catch {
    return false;
  }
}

/**
 * Convert HSL string to hex. Useful for color pickers.
 * Input: "hsl(220 14% 96%)" or "220 14% 96%"
 */
export function hslToHex(hsl: string): string {
  const match = hsl.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!match) return "#000000";
  const h = parseFloat(match[1]) / 360;
  const s = parseFloat(match[2]) / 100;
  const l = parseFloat(match[3]) / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) =>
    Math.round(c * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Convert hex color to HSL string.
 * Input: "#1a2b3c" → "hsl(210 38% 17%)"
 */
export function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "hsl(0 0% 0%)";
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `hsl(${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;
}
